import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router';
import { ArtifactLink } from '../components/ArtifactLink';
import { ADDON_UPLOAD_KIND_LABELS, ARTIFACT_TYPE_LABELS } from '@thottbot/shared';
import { listPendingShots, uploadShot } from '../api/addon-shots';
import { uploadAddonFile } from '../api/addon-upload';
import { apiGet, useAddonInfo, useAddonUpload } from '../api/client';
import { LoginGate } from '../components/LoginGate';
import { relativeTime } from '../util/time';
import { findThottbotFiles, type FoundFile, type ScreenshotDir } from '../watch/discover';
import {
  createShotUploader,
  type ShotLogEntry,
  type ShotSnapshot,
  type ShotUploader,
} from '../watch/shot-uploader';
import { browserFolderAccess, resolveWatchOverrides } from '../watch/folder-access';
import type { FolderAccess, FolderHandle, WatchTiming } from '../watch/types';
import { createWatcher, type LogEntry, type WatchSnapshot, type Watcher } from '../watch/watcher';

const EXPECTED_LAYOUT =
  'World of Warcraft/_classic_beta_/WTF/Account/<ACCOUNT>/SavedVariables/Thottbot.lua';
const SESSION_POLL_MS = 5000;

/**
 * `/addon/watch`: pick the WoW folder once, keep the tab open, and the page uploads every
 * Thottbot.lua the game writes. Chrome and Edge only (File System Access API).
 */
export function AddonWatch() {
  const overrides = resolveWatchOverrides();
  const access = overrides.folderAccess ?? browserFolderAccess;
  return (
    <div className="page" data-testid="addon-watch-page">
      <title>Auto-upload addon data</title>
      <h1>Auto-upload addon data</h1>
      {access.supported ? (
        <>
          <p>
            Pick your WoW folder once and keep this tab open while you play. Every time the game
            writes <code>Thottbot.lua</code> (on <code>/reload</code> or logout) it is uploaded for
            you, and so are the client&rsquo;s cache files (<code>questcache.wdb</code>,{' '}
            <code>creaturecache.wdb</code>, <code>gameobjectcache.wdb</code> and{' '}
            <code>DBCache.bin</code>, the quests, creatures, objects and item hotfixes your client
            has seen). Screenshots taken with the addon&rsquo;s camera button (or{' '}
            <code>/thottbot shot</code>) go up too, to the page of what they show, once the game
            has written <code>Thottbot.lua</code>; no other screenshot is ever read. Nothing
            uploads while the tab is closed. When the browser asks, tick
            &ldquo;Allow on every visit&rdquo; so the next visit starts watching without the picker.
            Prefer to upload by hand? Use <Link to="/addon/upload">the upload page</Link>.
          </p>
          <LoginGate action="auto-upload addon data">
            <WatchPanel access={access} timing={overrides.timing} />
          </LoginGate>
        </>
      ) : (
        <div className="notice" role="status" data-testid="watch-unsupported">
          Automatic upload needs Chrome or Edge on the computer that runs the game: only those
          browsers can watch a folder. On this browser,{' '}
          <Link to="/addon/upload">upload the file by hand</Link> instead.
        </div>
      )}
    </div>
  );
}

type Phase =
  'loading' | 'pick' | 'needs_permission' | 'denied' | 'scanning' | 'no_files' | 'watching';

const EMPTY_SNAPSHOT: WatchSnapshot = {
  state: 'watching',
  lastCheckAt: null,
  nextUploadAt: null,
  files: [],
  log: [],
  uploading: null,
};
const EMPTY_SHOTS: ShotSnapshot = { state: 'watching', waiting: 0, uploading: null, log: [] };
const noopSubscribe = () => () => {};

function WatchPanel({ access, timing }: { access: FolderAccess; timing?: Partial<WatchTiming> }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [folder, setFolder] = useState<FolderHandle | null>(null);
  const [cacheReachable, setCacheReachable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [watcher, setWatcher] = useState<Watcher | null>(null);
  const watcherRef = useRef<Watcher | null>(null);
  const [shots, setShots] = useState<ShotUploader | null>(null);
  const [shotDirs, setShotDirs] = useState<ScreenshotDir[]>([]);
  const shotsRef = useRef<ShotUploader | null>(null);
  const snapshot = useSyncExternalStore(
    watcher ? watcher.subscribe : noopSubscribe,
    watcher ? watcher.snapshot : () => EMPTY_SNAPSHOT,
  );
  const shotSnapshot = useSyncExternalStore(
    shots ? shots.subscribe : noopSubscribe,
    shots ? shots.snapshot : () => EMPTY_SHOTS,
  );

  const stopWatcher = useCallback(() => {
    watcherRef.current?.stop();
    watcherRef.current = null;
    setWatcher(null);
    shotsRef.current?.stop();
    shotsRef.current = null;
    setShots(null);
  }, []);

  const startWatching = useCallback(
    async (handle: FolderHandle) => {
      stopWatcher();
      setFolder(handle);
      setPhase('scanning');
      setError(null);
      let files: FoundFile[];
      let dirs: ScreenshotDir[];
      try {
        const found = await findThottbotFiles(handle);
        files = found.files;
        dirs = found.screenshotDirs;
        setCacheReachable(found.cacheReachable);
        setShotDirs(dirs);
      } catch (err) {
        setError(`Could not read the folder: ${(err as Error).message}`);
        setPhase('pick');
        return;
      }
      if (files.length === 0) {
        setPhase('no_files');
        return;
      }
      const w = createWatcher({
        files,
        timing,
        upload: (blob, file, onProgress) =>
          uploadAddonFile(blob, 'watcher', { onProgress, filename: file.handle.name }),
      });
      watcherRef.current = w;
      setWatcher(w);
      w.start();
      if (dirs.length) {
        const s = createShotUploader({
          dirs,
          listPending: listPendingShots,
          upload: uploadShot,
          timing: timing && { checkMs: timing.pollMs, spacingMs: timing.spacingMs },
        });
        shotsRef.current = s;
        setShots(s);
        s.start();
      }
      setPhase('watching');
    },
    [stopWatcher, timing],
  );

  // On mount: resume a remembered folder when the browser kept the grant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const remembered = await access.remembered();
      if (cancelled) return;
      if (!remembered) {
        setPhase('pick');
        return;
      }
      setFolder(remembered);
      const permission = await remembered.queryPermission({ mode: 'read' });
      if (cancelled) return;
      if (permission === 'granted') await startWatching(remembered);
      else setPhase(permission === 'prompt' ? 'needs_permission' : 'denied');
    })();
    return () => {
      cancelled = true;
      stopWatcher();
    };
  }, [access, startWatching, stopWatcher]);

  // After a 401, notice a login done in another tab and carry on.
  const signedOut = snapshot.state === 'signed_out' || shotSnapshot.state === 'signed_out';
  useEffect(() => {
    if (!signedOut) return;
    const timer = setInterval(async () => {
      try {
        const me = await apiGet<{ user: unknown | null }>('/api/me');
        if (me.user) {
          watcher?.sessionRestored();
          shots?.sessionRestored();
        }
      } catch {
        /* still signed out or offline; try again next interval */
      }
    }, SESSION_POLL_MS);
    return () => clearInterval(timer);
  }, [signedOut, watcher, shots]);

  // A Thottbot.lua upload may carry new screenshot records: look for their files right away.
  const lastAddonAt = snapshot.log.find(
    (e) => e.file === 'addon' && (e.kind === 'accepted' || e.kind === 'duplicate'),
  )?.at;
  useEffect(() => {
    if (lastAddonAt !== undefined) shots?.checkNow();
  }, [lastAddonAt, shots]);

  const pick = async () => {
    setError(null);
    let handle: FolderHandle | null;
    try {
      handle = await access.pick();
    } catch (err) {
      setError(`The folder picker failed: ${(err as Error).message}`);
      return;
    }
    if (!handle) return;
    await access.remember(handle);
    await startWatching(handle);
  };

  const resume = async () => {
    if (!folder) return;
    const permission = await folder.requestPermission({ mode: 'read' });
    if (permission === 'granted') await startWatching(folder);
    else setPhase('denied');
  };

  const forget = async () => {
    stopWatcher();
    await access.forget();
    setFolder(null);
    setPhase('pick');
  };

  if (phase === 'loading') return <p className="muted">Checking for a remembered folder...</p>;

  if (phase === 'pick' || phase === 'denied') {
    return (
      <section className="section">
        {phase === 'denied' && (
          <div className="notice error" role="alert">
            The browser did not grant access to {folder?.name ?? 'the remembered folder'}. Pick the
            folder again to continue.
          </div>
        )}
        {error && (
          <div className="notice error" role="alert">
            {error}
          </div>
        )}
        <div className="actions">
          <button type="button" onClick={pick} data-testid="watch-pick">
            Choose WoW folder
          </button>
          <span className="muted">
            Pick the game folder or <code>_classic_beta_</code> to include the cache files;{' '}
            <code>WTF</code>, <code>Account</code>, one account, or <code>SavedVariables</code>{' '}
            reach only <code>Thottbot.lua</code>. Read access only.
          </span>
        </div>
      </section>
    );
  }

  if (phase === 'needs_permission') {
    return (
      <section className="section">
        <div className="watch-status" data-testid="watch-status">
          <strong>Needs permission</strong> for <code>{folder?.name}</code>. The browser did not
          keep the grant; press the button to allow reading it again (no picker needed).
        </div>
        <div className="actions">
          <button type="button" onClick={resume} data-testid="watch-resume">
            Resume watching
          </button>
          <button type="button" onClick={forget} data-testid="watch-forget">
            Forget this folder
          </button>
        </div>
      </section>
    );
  }

  if (phase === 'scanning')
    return <p className="muted">Looking for Thottbot.lua under {folder?.name}...</p>;

  if (phase === 'no_files') {
    return (
      <section className="section">
        <div className="notice error" role="alert" data-testid="watch-no-files">
          No <code>Thottbot.lua</code> under <code>{folder?.name}</code>. The file is written when
          you log out or <code>/reload</code> with the addon enabled; it lives at{' '}
          <code>{EXPECTED_LAYOUT}</code>.
        </div>
        <div className="actions">
          <button type="button" onClick={pick} data-testid="watch-pick">
            Choose a different folder
          </button>
          <button type="button" onClick={() => folder && startWatching(folder)}>
            Look again
          </button>
        </div>
      </section>
    );
  }

  return (
    <WatchingView
      folderName={folder?.name ?? ''}
      cacheReachable={cacheReachable}
      snapshot={snapshot}
      shotDirs={shotDirs}
      shotSnapshot={shotSnapshot}
      onPause={() => {
        watcher?.pause();
        shots?.pause();
      }}
      onResume={() => {
        watcher?.resume();
        shots?.resume();
      }}
      onForget={forget}
    />
  );
}

const STATE_WORD = {
  watching: 'Watching',
  paused: 'Paused',
  signed_out: 'Signed out',
  backing_off: 'Waiting to retry',
} as const;

function WatchingView({
  folderName,
  cacheReachable,
  snapshot,
  shotDirs,
  shotSnapshot,
  onPause,
  onResume,
  onForget,
}: {
  folderName: string;
  cacheReachable: boolean;
  snapshot: WatchSnapshot;
  shotDirs: ScreenshotDir[];
  shotSnapshot: ShotSnapshot;
  onPause: () => void;
  onResume: () => void;
  onForget: () => void;
}) {
  const now = Date.now();
  const next =
    snapshot.nextUploadAt && snapshot.nextUploadAt > now
      ? Math.ceil((snapshot.nextUploadAt - now) / 1000)
      : null;
  return (
    <>
      <section className="section">
        {/* Only the state word is a live region: the last-check time changes on every poll. */}
        <div className="watch-status" data-testid="watch-status" data-state={snapshot.state}>
          <strong aria-live="polite">{STATE_WORD[snapshot.state]}</strong> <code>{folderName}</code>
          {snapshot.lastCheckAt !== null && (
            <> &middot; last check {relativeTime(new Date(snapshot.lastCheckAt).toISOString())}</>
          )}
          {snapshot.uploading && (
            <>
              {' '}
              &middot; uploading {snapshot.uploading.account} ({snapshot.uploading.progress}%)
            </>
          )}
          {!snapshot.uploading && next !== null && <> &middot; next upload in {next} s</>}
          {snapshot.state === 'signed_out' && (
            <>
              {' '}
              &middot; <Link to="/login?next=%2Faddon%2Fwatch">Sign in again</Link> to continue
            </>
          )}
        </div>
        <div className="actions">
          {snapshot.state === 'paused' ? (
            <button type="button" onClick={onResume} data-testid="watch-pause">
              Resume
            </button>
          ) : (
            <button
              type="button"
              onClick={onPause}
              data-testid="watch-pause"
              disabled={snapshot.state === 'signed_out'}
            >
              Pause
            </button>
          )}
          <button type="button" onClick={onForget} data-testid="watch-forget">
            Forget this folder
          </button>
          <span className="muted">Keep this tab open while you play.</span>
        </div>
        {!cacheReachable && (
          <div className="notice" role="status" data-testid="watch-cache-unreachable">
            Only <code>Thottbot.lua</code> is watched from here. Pick the game folder or{' '}
            <code>_classic_beta_</code> to include the cache files.
          </div>
        )}
      </section>

      <section className="section">
        <h2>Watched files</h2>
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Kind</th>
                <th scope="col">File</th>
                <th scope="col">Size</th>
                <th scope="col">Written</th>
                <th scope="col">State</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.files.map((f) => (
                <tr key={f.path} data-testid="watch-file" data-phase={f.phase} data-kind={f.kind}>
                  <td>{f.account}</td>
                  <td>{ADDON_UPLOAD_KIND_LABELS[f.kind]}</td>
                  <td>
                    <code>{f.path}</code>
                  </td>
                  <td>{f.size === null ? '-' : `${Math.max(1, Math.round(f.size / 1024))} KB`}</td>
                  <td>
                    {f.lastModified === null
                      ? '-'
                      : relativeTime(new Date(f.lastModified).toISOString())}
                  </td>
                  <td>
                    {PHASE_WORD[f.phase]}
                    {f.lastResult && <span className="muted"> - {f.lastResult}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ScreenshotsSection dirs={shotDirs} snapshot={shotSnapshot} />

      <section className="section">
        <h2>Uploads this visit</h2>
        {snapshot.log.length === 0 ? (
          <p className="muted">Nothing yet.</p>
        ) : (
          <ul className="watch-log">
            {snapshot.log.map((entry) => (
              <LogRow
                key={`${entry.at}-${entry.account}-${entry.file}-${entry.kind}`}
                entry={entry}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function ScreenshotsSection({ dirs, snapshot }: { dirs: ScreenshotDir[]; snapshot: ShotSnapshot }) {
  return (
    <section className="section" data-testid="watch-shots">
      <h2>Screenshots</h2>
      {dirs.length === 0 ? (
        <p className="muted" data-testid="watch-shots-none">
          No <code>Screenshots</code> folder here. Pick the game folder or{' '}
          <code>_classic_beta_</code> to upload screenshots taken with the addon&rsquo;s camera
          button.
        </p>
      ) : (
        <>
          <p>
            Watching{' '}
            {dirs.map((d, i) => (
              <span key={d.path}>
                {i > 0 && ', '}
                <code>{d.path}</code>
              </span>
            ))}{' '}
            for the addon button&rsquo;s screenshots; other screenshots are never read.{' '}
            <span data-testid="watch-shots-waiting" data-waiting={snapshot.waiting}>
              {snapshot.waiting === 0
                ? 'None waiting for a file.'
                : `${snapshot.waiting} waiting for ${snapshot.waiting === 1 ? 'its file' : 'their files'}.`}
            </span>
            {snapshot.uploading && <span className="muted"> Uploading {snapshot.uploading}...</span>}
          </p>
          {snapshot.log.length > 0 && (
            <ul className="watch-log">
              {snapshot.log.map((entry) => (
                <ShotRow key={`${entry.at}-${entry.stem}-${entry.kind}`} entry={entry} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function ShotRow({ entry }: { entry: ShotLogEntry }) {
  const bad = entry.kind === 'rejected' || entry.kind === 'error' || entry.kind === 'unauthorized' || entry.kind === 'dropped';
  return (
    <li data-testid="watch-shot-entry" data-kind={entry.kind} data-file="screenshot">
      <span className="muted">{relativeTime(new Date(entry.at).toISOString())}</span>{' '}
      <code>{entry.stem}</code>: {bad ? <span className="error">{entry.message}</span> : entry.message}
      {entry.artifact && (
        <>
          {' '}
          to <ArtifactLink type={entry.artifact.type} slug={entry.artifact.slug} name={entry.artifact.name} />{' '}
          <span className="muted">({ARTIFACT_TYPE_LABELS[entry.artifact.type].singular})</span>
        </>
      )}
    </li>
  );
}

const PHASE_WORD = {
  idle: 'up to date',
  changed: 'changed',
  settling: 'changed, waiting for the game to finish writing',
  queued: 'waiting to upload',
  uploading: 'uploading',
  rejected: 'refused until it changes',
} as const;

function LogRow({ entry }: { entry: LogEntry }) {
  const info = useAddonInfo();
  return (
    <li data-testid="watch-log-entry" data-kind={entry.kind} data-file={entry.file}>
      <span className="muted">{relativeTime(new Date(entry.at).toISOString())}</span>{' '}
      <strong>{entry.account}</strong>{' '}
      <span className="muted">({ADDON_UPLOAD_KIND_LABELS[entry.file].toLowerCase()})</span>:{' '}
      {entry.kind === 'rejected' || entry.kind === 'error' || entry.kind === 'unauthorized' ? (
        <span className="error">{entry.message}</span>
      ) : entry.kind === 'accepted' && entry.uploadId ? null : ( // the server result below says it all
        entry.message
      )}
      {entry.rejectedCode === 'addon_outdated' && info.data && (
        <>
          {' '}
          <a href={info.data.downloadUrl} download>
            Download the current addon
          </a>
        </>
      )}
      {entry.uploadId && <LogUpload id={entry.uploadId} />}
    </li>
  );
}

/** The server-side result of one upload, polled while it is still merging. */
function LogUpload({ id }: { id: string }) {
  const q = useAddonUpload(id);
  if (!q.data) return <span className="muted">uploaded...</span>;
  const u = q.data;
  if (u.status === 'processing') return <span className="muted">uploaded, processing...</span>;
  if (u.status === 'failed')
    return (
      <span className="error">uploaded, but processing failed: {u.error ?? 'unknown error'}</span>
    );
  const art = u.counts.artifacts;
  return (
    <span data-testid="watch-log-result">
      Upload {u.status === 'applied' ? 'complete' : u.status}
      {art && `: ${art.added} added, ${art.updated} updated, ${art.skipped} skipped`}
      {u.artifacts.length > 0 && (
        <>
          {' '}
          (
          {u.artifacts.slice(0, 5).map((a, i) => (
            <span key={`${a.type}/${a.slug}`}>
              {i > 0 && ', '}
              <ArtifactLink type={a.type} slug={a.slug} name={a.name} />{' '}
              <span className="muted">({ARTIFACT_TYPE_LABELS[a.type].singular})</span>
            </span>
          ))}
          {u.artifacts.length > 5 && `, and ${u.artifacts.length - 5} more`})
        </>
      )}
    </span>
  );
}
